import React, { useState, useEffect, useCallback, useMemo } from 'react';
import negocioQuantificacaoService from '../services/negocioQuantificacao.service';
import tipoAtividadeService        from '../services/tipoAtividade.service';
import Modal                       from './Modal';

// ─── Cores e estilos ────────────────────────────────────────────────────────
const COR = {
  azul:     '#003399',
  azulBg:   '#eff6ff',
  borda:    '#e8eaed',
  texto:    '#1a1a2e',
  muted:    '#888',
  branco:   '#fff',
  verde:    '#1D9E75',
  verdeBg:  '#f0fff4',
  vermelho: '#e53935',
  vermBg:   '#fff0f0',
  amarelo:  '#d97706',
  amlBg:    '#fffbeb',
  cinzaBg:  '#fafafa',
};

const inputStyle = {
  width: '100%', height: '32px', padding: '0 8px',
  border: `1px solid ${COR.borda}`, borderRadius: '4px',
  fontSize: '13px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
  fontFamily: "'Manrope', sans-serif",
};

const inputDisabledStyle = {
  ...inputStyle, backgroundColor: COR.cinzaBg, color: COR.muted,
};

// ─── Ícones SVG dos botões ───────────────────────────────────────────────────
const svgProps = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const SvgArquivo = ({ s = 14 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" {...svgProps} style={{ display: 'block' }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const SvgMais = ({ s = 14 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" {...svgProps} style={{ display: 'block' }}>
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const SvgSalvar = ({ s = 14 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" {...svgProps} style={{ display: 'block' }}>
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
  </svg>
);

// ─── Helpers ────────────────────────────────────────────────────────────────
function formatBR(n, casas = 2) {
  if (n === null || n === undefined || n === '') return '';
  const num = parseFloat(n);
  if (isNaN(num)) return '';
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: casas, maximumFractionDigits: casas,
  });
}

// Máscara calculadora: interpreta dígitos como centavos e formata em BR
// Ex: "8" → "0,08", "800" → "8,00", "1500000" → "15.000,00"
function centavosParaBR(input) {
  if (input === '' || input === null || input === undefined) return '';
  const digitos = String(input).replace(/\D/g, '');
  if (!digitos) return '';
  const num = parseInt(digitos, 10) / 100;
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function parseBR(v) {
  if (v === '' || v === null || v === undefined) return 0;
  const norm = String(v).replace(/\./g, '').replace(',', '.');
  const num = parseFloat(norm);
  return isNaN(num) ? 0 : num;
}

// Linha vazia pra grid
const LINHA_VAZIA = () => ({
  topico: '', topico_descricao: '',
  subtopico: '', subtopico_descricao: '',
  tipo_atividade_id: '', horas: 0,
});

// ═══════════════════════════════════════════════════════════════════════════════
function QuantificacaoModal({ aberto, negocio, anexoDos010, usuarioId, onFechar, onSucesso }) {
  const [carregando, setCarregando]   = useState(false);
  const [salvando, setSalvando]       = useState(false);
  const [parseando, setParseando]     = useState(false);
  const [erro, setErro]               = useState('');
  const [sucesso, setSucesso]         = useState('');

  // Cabeçalho de horas
  const [hrsEspec, setHrsEspec]       = useState('');
  const [pctGestao, setPctGestao]     = useState('');
  const [hrsConting, setHrsConting]   = useState('');
  // Valor hora (editável - vem do cabeçalho da quantificação ou do cliente vigente)
  const [valorHora, setValorHora]     = useState('');

  // Grid de itens
  const [itens, setItens]             = useState([]);

  // Tipos de atividade
  const [tipos, setTipos]             = useState([]);

  // Somente leitura: o backend marca quando o usuário é o vendedor do negócio
  // (vê a quantificação, mas não edita/salva/importa)
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  // Último DOS010 anexado ao negócio (vem do backend em obter) — usado pra
  // importar os itens quando a tela não foi aberta logo após o upload
  const [ultimoDos010, setUltimoDos010] = useState(null);

  // ─── Versionamento ──────────────────────────────────────────────────────
  // versaoVisualizada=null → versão atual (a maior); senão é histórica (read-only)
  const [versaoVisualizada, setVersaoVisualizada] = useState(null);
  const [versaoAtiva, setVersaoAtiva] = useState(null);     // qual está aberta
  const [ehHistorica, setEhHistorica] = useState(false);    // se a aberta é histórica
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [versoes, setVersoes] = useState([]);

  // ─── Bloqueia edição se: versão histórica OU negócio em F/E/D ────────────
  // Negócios fechados, encerrados ou perdidos não podem mais ter quantificação alterada
  const negocioFechado = ['F', 'E', 'D'].includes(negocio?.etapa_comercial);
  const ehBloqueada = ehHistorica || negocioFechado || somenteLeitura;
  const motivoBloqueio = ehHistorica
    ? `versão histórica (v${versaoAtiva})`
    : somenteLeitura
      ? 'você é o vendedor do negócio (acesso somente leitura)'
      : negocioFechado
      ? `negócio em etapa "${
          negocio?.etapa_comercial === 'F' ? 'Fechado' :
          negocio?.etapa_comercial === 'E' ? 'Encerrado' : 'Perdido'
        }"`
      : null;

  // Carrega dados ao abrir o modal
  const carregar = useCallback(async () => {
    if (!negocio?.id) return;
    // Tipos de atividade: carrega em separado — uma falha aqui não pode quebrar a
    // quantificação, nem uma falha da quantificação pode zerar o combo de Tipo.
    tipoAtividadeService.listar()
      .then(lista => setTipos(Array.isArray(lista) ? lista : []))
      .catch(e => console.error('Erro ao carregar tipos de atividade:', e));

    try {
      setCarregando(true);
      setErro('');
      const quant = await negocioQuantificacaoService.obter(negocio.id, usuarioId, versaoVisualizada);
      setHrsEspec(formatBR(quant.cabecalho.hrs_especificacao));
      setPctGestao(formatBR(quant.cabecalho.pct_gestao));
      setHrsConting(formatBR(quant.cabecalho.hrs_contingencia));
      setValorHora(formatBR(quant.cabecalho.valor_hora));
      setVersaoAtiva(quant.versao_ativa || quant.cabecalho.versao || '01');
      setEhHistorica(!!quant.eh_historica);
      setSomenteLeitura(!!quant.somente_leitura);
      setUltimoDos010(quant.ultimo_dos010 || null);
      setItens(quant.itens.length > 0
        ? quant.itens.map(i => ({ ...i, horas: formatBR(i.horas) }))
        : []
      );
    } catch (err) {
      console.error('Erro ao carregar quantificação:', err);
      if (err.response?.status === 403) {
        setErro(err.response.data?.mensagem
          || 'Acesso negado. Apenas Responsáveis Técnicos vinculados ao projeto podem acessar.');
      } else {
        setErro('Erro ao carregar dados.');
      }
    } finally {
      setCarregando(false);
    }
  }, [negocio?.id, usuarioId, versaoVisualizada]);

  useEffect(() => {
    if (aberto) carregar();
  }, [aberto, carregar]);

  // Reseta versão visualizada quando o modal é reaberto
  useEffect(() => {
    if (aberto) setVersaoVisualizada(null);
  }, [aberto]);

  // Carrega lista de versões pra exibir histórico
  const abrirHistorico = async () => {
    try {
      const lista = await negocioQuantificacaoService.listarVersoes(negocio.id, usuarioId);
      setVersoes(lista);
      setHistoricoAberto(true);
    } catch (err) {
      console.error('Erro ao listar versões:', err);
      alert('Erro ao carregar histórico de versões.');
    }
  };

  // Volta pra versão atual (a maior)
  const voltarParaAtual = () => {
    setVersaoVisualizada(null);
    setHistoricoAberto(false);
  };

  // ─── Parser DOS010 ────────────────────────────────────────────────────────
  // Anexo usado na importação: o do upload recém-feito ou, na falta dele,
  // o último DOS010 anexado ao negócio
  const anexoParaImportar = anexoDos010?.id ? anexoDos010 : ultimoDos010;

  // Botão de importar aparece quando veio de um upload de DOS010 OU quando
  // ainda não há nenhum item informado e existe um DOS010 anexado
  const podeImportarDos010 = !ehBloqueada && !!anexoParaImportar?.id
    && (!!anexoDos010?.id || itens.length === 0);

  const parsearAnexo = async () => {
    if (!anexoParaImportar?.id) {
      return setErro('Nenhum anexo DOS010 disponível para parser.');
    }
    if (itens.length > 0) {
      if (!window.confirm('Já existem itens cadastrados. Eles serão substituídos pelos itens extraídos do PDF. Continuar?')) {
        return;
      }
    }

    setParseando(true);
    setErro('');
    try {
      const res = await negocioQuantificacaoService.parsear(negocio.id, anexoParaImportar.id, usuarioId);
      if (res.itens && res.itens.length > 0) {
        setItens(res.itens.map(i => ({ ...i, horas: '0,00' })));
        setSucesso(res.observacao);
        setTimeout(() => setSucesso(''), 3000);
      } else {
        setErro(res.observacao || 'Nenhum tópico identificado no PDF.');
      }
    } catch (err) {
      console.error('Erro ao parsear:', err);
      setErro(err.response?.data?.mensagem || 'Erro ao parsear DOS010.');
    } finally {
      setParseando(false);
    }
  };

  // ─── Edição da grid ───────────────────────────────────────────────────────
  const atualizarItem = (idx, campo, valor) => {
    setItens(arr => arr.map((it, i) => i === idx ? { ...it, [campo]: valor } : it));
  };

  const moverItem = (idx, dir) => {
    setItens(arr => {
      const novo = [...arr];
      const alvo = idx + dir;
      if (alvo < 0 || alvo >= novo.length) return novo;
      [novo[idx], novo[alvo]] = [novo[alvo], novo[idx]];
      return novo;
    });
  };

  const removerItem = (idx) => {
    setItens(arr => arr.filter((_, i) => i !== idx));
  };

  const adicionarItem = () => {
    setItens(arr => [...arr, LINHA_VAZIA()]);
  };

  // ─── Cálculos em tempo real ───────────────────────────────────────────────
  const calculados = useMemo(() => {
    const hrsTec = itens.reduce((s, it) => s + parseBR(it.horas), 0);
    const pct = parseBR(pctGestao);
    // Horas de gestão sempre arredondadas pra cima (decisão de produto)
    const gestao = Math.round(hrsTec * (pct / 100));
    const total = hrsTec
                + parseBR(hrsEspec)
                + parseBR(hrsConting)
                + gestao;
    const vh = parseBR(valorHora);
    const valorBruto = total * vh;
    return {
      hrsTec, gestao, total, valorBruto, valorHora: vh,
    };
  }, [itens, hrsEspec, pctGestao, hrsConting, valorHora]);

  // ─── Salvar ───────────────────────────────────────────────────────────────
  const salvar = async () => {
    setErro(''); setSucesso('');

    // Validações simples
    const itensValidos = itens.filter(it =>
      it.subtopico_descricao && it.subtopico_descricao.trim()
    );

    if (itensValidos.length === 0)
      return setErro('Adicione pelo menos um item à quantificação.');

    setSalvando(true);
    try {
      const payload = {
        hrs_especificacao: parseBR(hrsEspec),
        pct_gestao:        parseBR(pctGestao),
        hrs_contingencia:  parseBR(hrsConting),
        valor_hora:        parseBR(valorHora),
        versao:            versaoAtiva,
        // Se tem anexo DOS010 contexto (primeira quantificação criada
        // a partir de upload), passa pro backend vincular a versão a esse anexo
        anexo_id:          anexoDos010?.id || undefined,
        itens: itensValidos.map(it => ({
          topico:              it.topico,
          topico_descricao:    it.topico_descricao,
          subtopico:           it.subtopico,
          subtopico_descricao: it.subtopico_descricao,
          tipo_atividade_id:   it.tipo_atividade_id || null,
          horas:               parseBR(it.horas),
        })),
        usuario_id: usuarioId,
      };
      await negocioQuantificacaoService.salvar(negocio.id, payload);
      setSucesso('Quantificação salva com sucesso!');
      setTimeout(() => setSucesso(''), 2500);
      // Avisa o componente pai pra recarregar o negócio (valor_previsto, valor_hora,
      // pct_comissao foram atualizados pelo backend ao salvar a quantificação)
      if (onSucesso) onSucesso();
    } catch (err) {
      console.error('Erro ao salvar:', err);
      setErro(err.response?.data?.mensagem || 'Erro ao salvar quantificação.');
    } finally {
      setSalvando(false);
    }
  };

  if (!aberto) return null;

  // Z-index alto pra ficar acima do modal do negócio
  const Z_OVERLAY = 10000;
  const Z_MODAL   = 10001;

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <>
      <Modal
        aberto={aberto}
        titulo={(
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            Quantificação do Projeto · {negocio?.id}
            {versaoAtiva && (
              <span style={{
                fontSize: '12px', fontWeight: '500', padding: '2px 8px', borderRadius: '10px',
                backgroundColor: ehHistorica ? '#d97706' : 'rgba(255,255,255,0.2)', color: COR.branco,
              }}>
                v{versaoAtiva}{ehHistorica ? ' · histórica' : ''}
              </span>
            )}
          </span>
        )}
        onFechar={onFechar}
        rotina="crm"
        descricao="Itens de quantificação e horas do projeto, por versão."
        botoesNoTopo
        somenteLeitura
        largura="1400px"
        botoesExtras={(
          <button type="button" onClick={abrirHistorico} title="Ver histórico de versões" style={{
            padding: '8px 14px', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '5px',
            backgroundColor: 'transparent', color: '#fff', fontSize: '13px', fontWeight: '600',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px',
            fontFamily: "'Manrope', sans-serif", whiteSpace: 'nowrap',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
            Histórico
          </button>
        )}
      >
        <div style={{ fontFamily: "'Manrope', sans-serif" }}>
          {ehBloqueada && (
            <div style={{
              backgroundColor: '#fffbeb', border: '1px solid #d97706',
              borderRadius: '5px', color: '#92400e', fontSize: '12px',
              padding: '8px 10px', marginBottom: '10px',
              display: 'flex', alignItems: 'center', gap: '10px',
            }}>
              <span style={{ fontSize: '16px' }}>{ehHistorica ? '📜' : somenteLeitura ? '👁' : '🔒'}</span>
              <div style={{ flex: 1, fontWeight: '600' }}>
                {ehHistorica
                  ? `Visualizando versão histórica (v${versaoAtiva}) — somente leitura.`
                  : somenteLeitura
                    ? 'Acesso somente leitura: como vendedor do negócio você pode consultar a quantificação, mas não alterá-la.'
                    : `Quantificação bloqueada: ${motivoBloqueio}. Edição não permitida.`}
              </div>
              {ehHistorica && (
                <button onClick={voltarParaAtual} style={{
                  padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                  backgroundColor: COR.branco, color: '#92400e',
                  border: '1px solid #d97706', borderRadius: '4px', cursor: 'pointer',
                }}>↩ Voltar pra atual</button>
              )}
            </div>
          )}
          {erro && (
            <div style={{
              backgroundColor: COR.vermBg, border: '1px solid #ffcccc',
              borderRadius: '5px', color: COR.vermelho, fontSize: '12px',
              padding: '8px 10px', marginBottom: '10px',
            }}>{erro}</div>
          )}
          {sucesso && (
            <div style={{
              backgroundColor: COR.verdeBg, border: '1px solid #a8e6c1',
              borderRadius: '5px', color: '#1d6a3a', fontSize: '12px',
              padding: '8px 10px', marginBottom: '10px',
            }}>{sucesso}</div>
          )}

          {carregando ? (
            <div style={{ textAlign: 'center', padding: '40px', color: COR.muted }}>
              Carregando...
            </div>
          ) : (
            <>
              {/* ───────── DADOS BASE + HORAS + VALORES ───────── */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 1.5fr 1fr',
                gap: '12px', marginBottom: '14px',
              }}>
                {/* DADOS BASE */}
                <Caixa titulo="Dados Base">
                  <Linha>
                    <Campo label="Projeto" largura="40%">
                      <input value={negocio?.id || ''} disabled
                        style={{ ...inputDisabledStyle, fontWeight: '600' }} />
                    </Campo>
                    <Campo label="Cliente" largura="60%">
                      <input value={negocio?.cliente_nome || ''} disabled
                        style={inputDisabledStyle} />
                    </Campo>
                  </Linha>
                  <Linha>
                    <Campo label="Descrição" largura="100%">
                      <input value={negocio?.descricao || ''} disabled
                        style={inputDisabledStyle} />
                    </Campo>
                  </Linha>
                </Caixa>

                {/* HORAS */}
                <Caixa titulo="Horas">
                  <Linha>
                    <Campo label="Especificação" largura="33%">
                      <input value={hrsEspec}
                        onChange={e => setHrsEspec(centavosParaBR(e.target.value))}
                        disabled={ehBloqueada}
                        style={ehBloqueada
                          ? { ...inputDisabledStyle, textAlign: 'right' }
                          : { ...inputStyle, textAlign: 'right' }}
                        placeholder="0,00" />
                    </Campo>
                    <Campo label="Gestão (%)" largura="33%">
                      <input value={pctGestao}
                        onChange={e => setPctGestao(centavosParaBR(e.target.value))}
                        disabled={ehBloqueada}
                        style={ehBloqueada
                          ? { ...inputDisabledStyle, textAlign: 'right' }
                          : { ...inputStyle, textAlign: 'right' }}
                        placeholder="10,00" />
                    </Campo>
                    <Campo label="Contingência" largura="33%">
                      <input value={hrsConting}
                        onChange={e => setHrsConting(centavosParaBR(e.target.value))}
                        disabled={ehBloqueada}
                        style={ehBloqueada
                          ? { ...inputDisabledStyle, textAlign: 'right' }
                          : { ...inputStyle, textAlign: 'right' }}
                        placeholder="0,00" />
                    </Campo>
                  </Linha>
                  <Linha>
                    <Campo label="Hrs. Técnicas" largura="33%">
                      <input value={formatBR(calculados.hrsTec)} disabled
                        style={{ ...inputDisabledStyle, textAlign: 'right', fontWeight: '600' }} />
                    </Campo>
                    <Campo label="Gestão Projeto" largura="33%">
                      <input value={formatBR(calculados.gestao, 0)} disabled
                        style={{ ...inputDisabledStyle, textAlign: 'right' }} />
                    </Campo>
                    <Campo label="Total de Horas" largura="33%">
                      <input value={formatBR(calculados.total)} disabled
                        style={{ ...inputDisabledStyle, textAlign: 'right', fontWeight: '700', color: COR.azul }} />
                    </Campo>
                  </Linha>
                </Caixa>

                {/* VALORES */}
                <Caixa titulo="Valores">
                  <Linha>
                    <Campo label="Valor Hora (R$)" largura="100%">
                      <input
                        value={valorHora}
                        onChange={e => setValorHora(centavosParaBR(e.target.value))}
                        disabled={ehBloqueada}
                        style={ehBloqueada
                          ? { ...inputDisabledStyle, textAlign: 'right', fontWeight: '600' }
                          : { ...inputStyle, textAlign: 'right', fontWeight: '600' }}
                        placeholder="0,00"
                      />
                    </Campo>
                  </Linha>
                  <Linha>
                    <Campo label="Valor Bruto" largura="100%">
                      <input
                        value={'R$ ' + formatBR(calculados.valorBruto)}
                        disabled
                        style={{
                          ...inputDisabledStyle, textAlign: 'right',
                          fontWeight: '700', color: COR.verde, fontSize: '14px',
                        }} />
                    </Campo>
                  </Linha>
                </Caixa>
              </div>

              {/* ───────── BOTÕES DE AÇÃO ───────── */}
              <div style={{
                display: 'flex', gap: '8px', marginBottom: '10px',
                paddingBottom: '10px', borderBottom: `1px solid ${COR.borda}`,
              }}>
                {podeImportarDos010 && (
                  <button onClick={parsearAnexo} disabled={parseando}
                    title={`Importar itens de: ${anexoParaImportar?.descricao || anexoParaImportar?.nome_original || 'DOS010'}`}
                    style={{
                    padding: '7px 14px', fontSize: '12px', fontWeight: '600',
                    border: `1px solid ${COR.amarelo}`, borderRadius: '5px',
                    backgroundColor: COR.amlBg, color: COR.amarelo,
                    cursor: parseando ? 'not-allowed' : 'pointer',
                    opacity: parseando ? 0.6 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                  }}>
                    {parseando ? 'Lendo PDF...' : <><SvgArquivo />Importar do DOS010</>}
                  </button>
                )}
                <button onClick={adicionarItem} disabled={ehBloqueada} style={{
                  padding: '7px 14px', fontSize: '12px', fontWeight: '600',
                  border: `1px solid ${COR.azul}`, borderRadius: '5px',
                  backgroundColor: ehBloqueada ? COR.cinzaBg : COR.azulBg,
                  color: ehBloqueada ? COR.muted : COR.azul,
                  cursor: ehBloqueada ? 'not-allowed' : 'pointer',
                  opacity: ehBloqueada ? 0.6 : 1,
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                }}>
                  <SvgMais />Nova linha
                </button>
                <div style={{ flex: 1 }} />
                {!ehBloqueada && (
                  <button onClick={salvar} disabled={salvando} style={{
                    padding: '7px 16px', fontSize: '12px', fontWeight: '600',
                    border: 'none', borderRadius: '5px',
                    backgroundColor: COR.verde, color: COR.branco,
                    cursor: salvando ? 'not-allowed' : 'pointer',
                    opacity: salvando ? 0.6 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                  }}>
                    {salvando ? 'Salvando...' : <><SvgSalvar />Salvar Quantificação</>}
                  </button>
                )}
              </div>

              {/* ───────── GRID DE ITENS ───────── */}
              <div style={{
                border: `1px solid ${COR.borda}`, borderRadius: '6px',
                overflow: 'hidden',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: COR.cinzaBg, borderBottom: `1px solid ${COR.borda}` }}>
                      <th style={ThStyle(60)}>Item</th>
                      <th style={ThStyle(140)}>Descrição</th>
                      <th style={ThStyle(70)}>Sub-Item</th>
                      <th style={ThStyle()}>Descrição</th>
                      <th style={ThStyle(150)}>Tipo</th>
                      <th style={ThStyle(80)}>Horas</th>
                      <th style={ThStyle(80)}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{
                          padding: '30px', textAlign: 'center', color: COR.muted,
                        }}>
                          Nenhum item ainda. Clique em "Nova linha" ou "Importar do DOS010".
                        </td>
                      </tr>
                    ) : itens.map((it, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${COR.borda}` }}>
                        <td style={TdStyle}>
                          <input value={it.topico || ''}
                            onChange={e => atualizarItem(idx, 'topico', e.target.value)}
                            disabled={ehBloqueada}
                            style={ehBloqueada ? inputDisabledStyle : inputStyle} placeholder="10.1" />
                        </td>
                        <td style={TdStyle}>
                          <input value={it.topico_descricao || ''}
                            onChange={e => atualizarItem(idx, 'topico_descricao', e.target.value)}
                            disabled={ehBloqueada}
                            style={ehBloqueada ? inputDisabledStyle : inputStyle} placeholder="DC-e" />
                        </td>
                        <td style={TdStyle}>
                          <input value={it.subtopico || ''}
                            onChange={e => atualizarItem(idx, 'subtopico', e.target.value)}
                            disabled={ehBloqueada}
                            style={ehBloqueada ? inputDisabledStyle : inputStyle} placeholder="10.1.1" />
                        </td>
                        <td style={TdStyle}>
                          <input value={it.subtopico_descricao || ''}
                            onChange={e => atualizarItem(idx, 'subtopico_descricao', e.target.value)}
                            disabled={ehBloqueada}
                            style={ehBloqueada ? inputDisabledStyle : inputStyle} placeholder="Descrição do sub-tópico" />
                        </td>
                        <td style={TdStyle}>
                          <ComboTipo
                            valor={it.tipo_atividade_id}
                            onChange={(v) => atualizarItem(idx, 'tipo_atividade_id', v)}
                            tipos={tipos}
                            disabled={ehBloqueada}
                          />
                        </td>
                        <td style={TdStyle}>
                          <input value={it.horas}
                            onChange={e => atualizarItem(idx, 'horas', centavosParaBR(e.target.value))}
                            disabled={ehBloqueada}
                            style={ehBloqueada
                              ? { ...inputDisabledStyle, textAlign: 'right' }
                              : { ...inputStyle, textAlign: 'right' }}
                            placeholder="0,00" />
                        </td>
                        <td style={TdStyle}>
                          <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
                            <button onClick={() => moverItem(idx, -1)}
                              disabled={idx === 0 || ehBloqueada}
                              title="Subir" style={BtnIconCss(idx === 0 || ehBloqueada)}>▲</button>
                            <button onClick={() => moverItem(idx, +1)}
                              disabled={idx === itens.length - 1 || ehBloqueada}
                              title="Descer" style={BtnIconCss(idx === itens.length - 1 || ehBloqueada)}>▼</button>
                            <button onClick={() => removerItem(idx)}
                              disabled={ehBloqueada}
                              title="Remover" style={{
                                ...BtnIconCss(ehBloqueada),
                                color: COR.vermelho,
                              }}>✕</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* ─── Painel de Histórico de Versões ─── */}
      {historicoAberto && (
        <>
          <div onClick={() => setHistoricoAberto(false)} style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 10002,
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%', maxWidth: '800px', maxHeight: '85vh',
            backgroundColor: COR.branco, borderRadius: '8px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            display: 'flex', flexDirection: 'column',
            zIndex: 10003, overflow: 'hidden',
            fontFamily: "'Manrope', sans-serif",
          }}>
            <div style={{
              padding: '14px 20px', borderBottom: `1px solid ${COR.borda}`,
              display: 'flex', alignItems: 'center', gap: '12px',
              backgroundColor: COR.azul, color: COR.branco,
            }}>
              <span style={{ fontSize: '18px' }}>📜</span>
              <span style={{ fontSize: '15px', fontWeight: '600', flex: 1 }}>
                Histórico de Versões da Quantificação
              </span>
              <button onClick={() => setHistoricoAberto(false)} style={{
                border: 'none', background: 'transparent',
                color: COR.branco, fontSize: '20px', cursor: 'pointer',
              }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px' }}>
              {versoes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: COR.muted, fontSize: '13px' }}>
                  Nenhuma versão cadastrada ainda.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: COR.cinzaBg }}>
                      <th style={ThEstilo}>Versão</th>
                      <th style={ThEstilo}>Hrs. Técnicas</th>
                      <th style={ThEstilo}>Hrs. Espec.</th>
                      <th style={ThEstilo}>Hrs. Cont.</th>
                      <th style={ThEstilo}>Gestão</th>
                      <th style={ThEstilo}>Total</th>
                      <th style={ThEstilo}>Vlr. Hora</th>
                      <th style={ThEstilo}>Vlr. Bruto</th>
                      <th style={ThEstilo}>Última alteração</th>
                      <th style={ThEstilo}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {versoes.map((v, i) => {
                      const ehAtual = i === 0; // primeira (DESC) é a atual
                      return (
                        <tr key={v.versao} style={{
                          borderBottom: `1px solid ${COR.borda}`,
                          backgroundColor: ehAtual ? '#f0fff4' : 'transparent',
                        }}>
                          <td style={TdEstilo}>
                            <strong>v{v.versao}</strong>
                            {ehAtual && <span style={{
                              marginLeft: '6px', fontSize: '10px', color: COR.verde, fontWeight: '600',
                            }}>(atual)</span>}
                          </td>
                          <td style={TdEstilo}>{formatBR(v.hrs_tecnicas)}</td>
                          <td style={TdEstilo}>{formatBR(v.hrs_especificacao)}</td>
                          <td style={TdEstilo}>{formatBR(v.hrs_contingencia)}</td>
                          <td style={TdEstilo}>{formatBR(v.gestao_projeto, 0)}</td>
                          <td style={{ ...TdEstilo, fontWeight: '700' }}>{formatBR(v.total_horas)}</td>
                          <td style={TdEstilo}>{formatBR(v.valor_hora)}</td>
                          <td style={{ ...TdEstilo, fontWeight: '700', color: COR.azul }}>
                            R$ {formatBR(v.valor_bruto)}
                          </td>
                          <td style={{ ...TdEstilo, fontSize: '11px', color: COR.muted }}>
                            {v.alterado_em ? new Date(v.alterado_em).toLocaleString('pt-BR') : '—'}
                            {v.alterado_por_nome && <><br />{v.alterado_por_nome}</>}
                          </td>
                          <td style={TdEstilo}>
                            <button onClick={() => {
                              setVersaoVisualizada(ehAtual ? null : v.versao);
                              setHistoricoAberto(false);
                            }} style={{
                              padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                              border: `1px solid ${COR.azul}`, borderRadius: '4px',
                              backgroundColor: COR.branco, color: COR.azul,
                              cursor: 'pointer',
                            }}>👁 Ver</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

const ThEstilo = {
  padding: '8px 6px', textAlign: 'left',
  borderBottom: '2px solid #e5e7eb', fontWeight: '600',
};

const TdEstilo = {
  padding: '8px 6px',
};

// ─── Sub-componentes ────────────────────────────────────────────────────────
function Caixa({ titulo, children }) {
  return (
    <div style={{
      border: `1px solid ${COR.borda}`, borderRadius: '6px',
      backgroundColor: COR.cinzaBg, padding: '8px 10px',
    }}>
      <div style={{
        fontSize: '10px', fontWeight: '700', color: COR.azul,
        textTransform: 'uppercase', letterSpacing: '0.4px',
        marginBottom: '6px',
      }}>{titulo}</div>
      {children}
    </div>
  );
}

function Linha({ children }) {
  return <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>{children}</div>;
}

function Campo({ label, largura = '100%', children }) {
  return (
    <div style={{ width: largura }}>
      <label style={{
        display: 'block', fontSize: '10px', fontWeight: '600',
        color: COR.muted, marginBottom: '2px',
      }}>{label}</label>
      {children}
    </div>
  );
}

function ComboTipo({ valor, onChange, tipos, disabled = false }) {
  const tipoAtual = tipos.find(t => t.id === valor);
  return (
    <select value={valor || ''} onChange={e => onChange(e.target.value)}
      disabled={disabled}
      style={{
        ...inputStyle, cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor: disabled ? COR.cinzaBg : (tipoAtual ? tipoAtual.cor + '22' : COR.branco),
        color: disabled ? COR.muted : (tipoAtual ? tipoAtual.cor : COR.texto),
        fontWeight: tipoAtual ? '700' : '400',
        borderColor: tipoAtual ? tipoAtual.cor : COR.borda,
      }}>
      <option value="">Selecione...</option>
      {tipos.map(t => (
        <option key={t.id} value={t.id} style={{
          color: t.cor, fontWeight: '700',
        }}>{t.descricao}</option>
      ))}
    </select>
  );
}

const ThStyle = (largura) => ({
  padding: '8px 6px', textAlign: 'left',
  fontSize: '10px', fontWeight: '700', color: COR.muted,
  textTransform: 'uppercase', letterSpacing: '0.4px',
  whiteSpace: 'nowrap',
  ...(largura ? { width: `${largura}px` } : {}),
});

const TdStyle = {
  padding: '4px 6px',
  verticalAlign: 'middle',
};

const BtnIconCss = (disabled) => ({
  border: 'none',
  background: 'transparent',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.3 : 1,
  fontSize: '12px',
  padding: '2px 6px',
  color: COR.azul,
});

export default QuantificacaoModal;
